import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import { toast } from "react-toastify";
import RotaResources from "../resources/RotaResources";
import ChecklistResources from "../resources/ChecklistResources";
import LocationResources from "../resources/LocationResources";
import { connect } from "react-redux";
import { logout } from "../redux/actions/LoggedInAction";
import { ACCESS_DENIED } from "../Messages";
import moment from "moment";

const PRODUCTION_ROLES = ['producer', 'directing', 'propresenter', 'live streaming', 'announcement']

function InProductionRoom({ isLoggedIn, loggedInUser, logout }) {
    const rotaResources = new RotaResources()
    const checklistResources = new ChecklistResources()
    const locationResources = new LocationResources()

    const [sundays, setSundays] = useState([])
    const [admins, setAdmins] = useState([])
    const [monthYear, setMonthYear] = useState({ monthName: '', year: '' })
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!isLoggedIn) return

        const currentDate = moment().format('DD-MM-YYYY HH:mm:ss')

        locationResources.get().then(locResp => {
            const locations = locResp['body'] || []
            const libertyPoint = locations.find(l => l.name.toLowerCase().includes('liberty point'))
            if (!libertyPoint) {
                setLoading(false)
                return
            }
            const libertyPointId = libertyPoint.id

            Promise.all([
                rotaResources.get_monthly_rota_user(currentDate, libertyPointId),
                checklistResources.get_production_room_admins()
            ]).then(([rotaResp, adminsResp]) => {
                setLoading(false)
                if (!rotaResp.error || rotaResp.error.length === 0) {
                    const body = rotaResp['body']
                    setSundays(body['sundays'] || [])
                    setMonthYear({ monthName: body['month_name'], year: body['year'] })
                }
                if (!adminsResp.error || adminsResp.error.length === 0) {
                    setAdmins(adminsResp['body'] || [])
                }
            }).catch(err => {
                setLoading(false)
                toast.error("Network error: " + err)
            })
        }).catch(err => {
            setLoading(false)
            toast.error("Network error: " + err)
        })
    }, [isLoggedIn])

    // Find the active Sunday: first one >= today, or the last one if all past
    const today = moment().startOf('day')
    const activeSunday = sundays.find(s => moment(s.date, 'YYYY-MM-DD').isSameOrAfter(today))
        || sundays[sundays.length - 1]
        || null

    // People in production roles for a specific service (not deduplicated — per service)
    const getServiceTeam = (service) => {
        if (!service.roles) return []
        const team = []
        Object.entries(service.roles).forEach(([roleName, users]) => {
            if (!PRODUCTION_ROLES.includes(roleName.toLowerCase())) return
            users.forEach(user => team.push({ ...user, role: roleName }))
        })
        return team
    }

    // Admin IDs already on the rota for this sunday (to avoid duplicates in the admin section)
    const getRotaUserIds = (sunday) => {
        const ids = new Set()
        sunday.services.forEach(service => {
            if (!service.roles) return
            Object.entries(service.roles).forEach(([roleName, users]) => {
                if (PRODUCTION_ROLES.includes(roleName.toLowerCase())) {
                    users.forEach(u => ids.add(u.id))
                }
            })
        })
        return ids
    }

    const PersonCard = ({ person }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e8edf2'
        }}>
            {person.image ? (
                <img src={person.image} alt=""
                    style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ffa800', flexShrink: 0 }} />
            ) : (
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#003165', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#ffa800', fontWeight: 'bold', fontSize: '13px' }}>
                        {person.firstname?.[0]}{person.lastname?.[0]}
                    </span>
                </div>
            )}
            <div>
                <div style={{ fontWeight: 'bold', color: '#003165', fontSize: '14px' }}>
                    {person.firstname} {person.lastname}
                </div>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {person.role}
                </div>
            </div>
        </div>
    )

    return (
        <Fragment>
            <Topbar />

            <div className="hero-area">
                <div className="page-banner parallax" style={{ backgroundColor: "#003165" }}>
                    <div className="container">
                        <div className="page-banner-text">
                            {isLoggedIn
                                ? <>
                                    <h1 style={{ color: "#ffa800" }} className="block-title"><strong>In Production Room</strong></h1>
                                    <p style={{ color: '#ffa800', fontSize: 'clamp(15px, 3.5vw, 18px)', fontWeight: 'bold', margin: '6px 0 0' }}>
                                        These are the only people expected in the room
                                    </p>
                                  </>
                                : <h1 style={{ color: "#ffa800" }} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div className="spacer-20" />

            {isLoggedIn ? (
                <div className="container" style={{ padding: '20px' }}>

                    {loading && (
                        <p style={{ color: '#666', textAlign: 'center', padding: '40px 0' }}>Loading...</p>
                    )}

                    {!loading && !activeSunday && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
                            No rota published for Liberty Point this month.
                        </div>
                    )}

                    {!loading && activeSunday && (() => {
                        const dateLabel = moment(activeSunday.date, 'YYYY-MM-DD').format('dddd DD MMMM YYYY')
                        const rotaUserIds = getRotaUserIds(activeSunday)
                        const adminExtras = admins.filter(a => !rotaUserIds.has(a.id))

                        return (
                            <>
                                {/* Date heading */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h2 style={{ color: '#003165', margin: 0 }}>
                                        <strong>Liberty Point — {monthYear.monthName} {monthYear.year}</strong>
                                    </h2>
                                    <div style={{
                                        display: 'inline-block',
                                        marginTop: '8px',
                                        backgroundColor: '#003165',
                                        color: '#ffa800',
                                        fontWeight: 'bold',
                                        fontSize: 'clamp(14px, 3.5vw, 18px)',
                                        padding: '6px 18px',
                                        borderRadius: '6px'
                                    }}>
                                        {dateLabel}
                                    </div>
                                </div>

                                {/* Services */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '20px'
                                }}>
                                    {activeSunday.services.map((service, svcIdx) => {
                                        const team = getServiceTeam(service)
                                        return (
                                            <div key={svcIdx} style={{
                                                backgroundColor: '#fff',
                                                borderRadius: '10px',
                                                border: '2px solid #003165',
                                                overflow: 'hidden'
                                            }}>
                                                {/* Service header */}
                                                <div style={{ backgroundColor: '#003165', padding: '10px 16px' }}>
                                                    <div style={{ color: '#ffa800', fontWeight: 'bold', fontSize: '15px' }}>
                                                        {service.service_name}
                                                    </div>
                                                    <div style={{ color: '#aac0d8', fontSize: '11px', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Who Should Be In The Room
                                                    </div>
                                                </div>

                                                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {team.length > 0 ? team.map((person, i) => (
                                                        <PersonCard key={i} person={person} />
                                                    )) : (
                                                        <p style={{ color: '#aaa', fontSize: '13px', margin: '8px 0', textAlign: 'center' }}>
                                                            No assignments yet
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Admins panel — shown once, alongside services */}
                                    {adminExtras.length > 0 && (
                                        <div style={{
                                            backgroundColor: '#fff',
                                            borderRadius: '10px',
                                            border: '2px solid #ffa800',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{ backgroundColor: '#ffa800', padding: '10px 16px' }}>
                                                <div style={{ color: '#003165', fontWeight: 'bold', fontSize: '15px' }}>
                                                    Admins
                                                </div>
                                                <div style={{ color: '#7a5200', fontSize: '11px', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Always In The Room
                                                </div>
                                            </div>
                                            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {adminExtras.map((admin, i) => (
                                                    <PersonCard key={i} person={{ ...admin, role: 'Admin' }} />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Disclaimer */}
                                <div style={{
                                    marginTop: '24px',
                                    backgroundColor: '#fff8e6',
                                    border: '1px solid #ffa800',
                                    borderRadius: '6px',
                                    padding: '10px 16px',
                                    fontSize: '13px',
                                    color: '#7a5200'
                                }}>
                                    This page automatically updates to the next upcoming Sunday after each Sunday passes.
                                </div>
                            </>
                        )
                    })()}

                    <div className="spacer-40" />
                </div>
            ) : <></>}
        </Fragment>
    )
}

const mapStateToProps = (state) => ({
    isLoggedIn: state.authReducer.isLoggedIn,
    loggedInUser: state.authReducer.user
})

const mapDispatchToProps = { logout }

export default connect(mapStateToProps, mapDispatchToProps)(InProductionRoom)
